/**
 * `AW46` · **El veredicto es NUESTRO; el estado sigue siendo de Meta.**
 *
 * `ADR-0247` abrió dos puertas de TIPO por `contracts/admin-api` —el veredicto
 * (`TemplateSendability`) y el literal que decide nuestra pregunta
 * (`TemplateApprovedStatus`)— **para que la pantalla no invente su propio
 * vocabulario de tres valores**. Este archivo es donde ese veredicto se arma, y
 * arma nada más que eso.
 *
 * ⛔ **El veredicto NO reemplaza al estado crudo.** `AW46` §`guard` ⑥: el
 * literal que Meta devolvió se muestra tal cual, porque el operador usa esta
 * pantalla para reconciliar contra el WhatsApp Manager, que muestra el
 * vocabulario de Meta. La glosa va AL LADO del literal, nunca EN SU LUGAR.
 *
 * ⭐⭐ **Y SON DOS DECISIONES, ESCRITAS EN DOS FUNCIONES Y EN DOS RENGLONES.**
 * `AW46` §`guard` ③ y `ADR-0240`: «sincronizada» y «aprobada» son dos hechos
 * distintos, y una lista que nunca se trajo **NO significa «no aprobada»** —
 * significa que de este brazo todavía no se sabe nada. Colapsar las dos en un
 * ternario de un solo renglón junta las dos preguntas en un lugar donde después
 * nadie las separa, y `contracts/admin-api`
 * ⟦por-que-el-borde-no-emite-el-veredicto⟧ lo deja escrito con todas las letras:
 * *«Son dos decisiones y se escriben como dos.»*
 *
 * ⚠️ **Y `no-sincronizada` es del BRAZO, no de la fila.** Sale de
 * `TemplateCatalogSnapshot.lastSyncedAt`, que es el hecho que lo dice, y no de
 * ninguna plantilla: si el brazo tiene filas, el brazo sincronizó.
 */

import type { TemplateSendability } from "@contracts/admin-api/index.ts"
import { ESTADO_APROBADA_EN_META } from "./borde-plantillas.ts"
import type { BotonDePlantilla, PlantillaDelBrazo } from "./borde-plantillas.ts"

/*
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔⛔ RASTRO · 23/9/2026, `R2-UI1` (`B13`, `RT 9`) · **ACÁ DECIDÍA LA
 * PANTALLA, Y DEJÓ DE HACERLO.** `ADR-0082`: redactar no es borrar.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ~~`export function faltaSincronizar(lastSyncedAt: string | null): TemplateSendability | null`~~
 * ~~`export function veredictoDelEstado(approvalStatus: string): TemplateSendability`~~
 * ~~`export function rotuloDelVeredicto(veredicto: TemplateSendability): string`~~
 *
 * Las dos primeras eran las «DOS DECISIONES, ESCRITAS EN DOS FUNCIONES Y EN DOS
 * RENGLONES» de `ADR-0247`, y **el análisis que las puso ahí sigue siendo
 * correcto**: «sincronizada» y «aprobada» son dos hechos distintos, y una lista
 * que nunca se trajo NO significa «no aprobada». Lo que cambió no es el análisis
 * sino **DÓNDE se contesta**.
 *
 * ⭐ `PRD_R2.md:845` · *«Que el estado diga “Aprobada” y el veredicto diga “No” no
 * puede pasar»*. Con `R2-F2` hay DOS lugares que responden la misma pregunta: esta
 * pantalla, derivando de `approvalStatus + lastSyncedAt`, y `canSend` en el motor
 * de envío (`R2-DS2`), derivando del mismo catálogo por su cuenta. **La primera
 * vez que diverjan**, el operador lee «Aprobada · Sí» acá y el envío sale
 * «Fallido · La plantilla no se puede usar», y ningún examen del repo puede ver la
 * diferencia porque cada lado está bien por separado.
 *
 * ⇒ el veredicto lo emite el BORDE —`DispatchTemplateRow.sendability`, decidido
 * sobre el mismo catálogo que usa el motor— y esta unidad **se reduce a texto**.
 * La tentación mide dos líneas y está a mano: `catalog` viaja al lado de
 * `sendability` en cada fila, así que recalcular sería un `if` sobre un campo que
 * ya está en el alcance. Por eso el argumento queda escrito acá y no en un ADR que
 * nadie abre al editar este archivo.
 *
 * ⚠️ **`estadoEnEspanol` NO se va**: traducir el vocabulario de Meta sigue siendo
 * de la pantalla (⟦el-vocabulario-ajeno-viaja-crudo⟧), y eso no es un veredicto.
 */

/**
 * ⭐⭐ **LOS CUATRO TEXTOS DE «¿Se puede enviar?», Y SON CUATRO SOBRE TRES
 * `kind`** (`PRD_R2.md:838-841`, `R2-F2-CA-06`…`CA-09`).
 *
 * ⛔⛔ **El cuarto texto NO es una cuarta variante del contrato, y no hay que
 * agregársela.** `TemplateSendability` tiene tres —`aprobada`, `no-aprobada`,
 * `no-sincronizada`— y son las tres que el MOTOR necesita para decidir si manda.
 * La pantalla necesita una distinción más, que es de REDACCIÓN y no de dominio:
 *
 *   · `no-sincronizada` **con** `lastSyncedAt` ⇒ *se sincronizó y esta plantilla
 *     no vino* — la plantilla desapareció del catálogo (`CA-08`);
 *   · `no-sincronizada` **sin** `lastSyncedAt` ⇒ *nunca se corrió una
 *     sincronización para este brazo* — de la plantilla no se sabe nada (`CA-09`).
 *
 * Los dos son «No» y los dos son el mismo `kind`; lo que cambia es **de quién es
 * la culpa**, y eso decide a dónde va el operador: al WhatsApp Manager en el
 * primero, al botón «Sincronizar plantillas» en el segundo. Meterlo en el contrato
 * le daría al motor una variante que no usa para nada.
 *
 * ⚠️ **`lastSyncedAt` es del BRAZO, no de la fila**, igual que antes: sale del
 * catálogo del brazo y vale para todas las filas de la tabla.
 *
 * ⛔ **El motivo NO repite el estado crudo** (`PRD_R2.md:847`): el estado real ya
 * se lee en la columna de al lado, y repetirlo sería la celda que se lee en vez de
 * compararse.
 */
export type TextoDelVeredicto = {
  /** «Sí» o «No». Es lo que la celda dice primero. */
  readonly respuesta: string
  /** El motivo, o `null` cuando la respuesta es «Sí» y no hay nada que explicar. */
  readonly motivo: string | null
}

export function textoDelVeredicto(
  sendability: TemplateSendability,
  lastSyncedAt: string | null,
): TextoDelVeredicto {
  switch (sendability.kind) {
    case "aprobada":
      return { respuesta: "Sí", motivo: null }
    case "no-aprobada":
      /*
       * ⭐ `ARCHIVED` cae acá y es correcto (`CA-10` + `PRD_R2.md:847`): el
       * veredicto dice «no está aprobada» y la columna de al lado muestra
       * «ARCHIVED», que es lo que el operador busca en el WhatsApp Manager.
       */
      return { respuesta: "No", motivo: "No está aprobada en Meta." }
    case "no-sincronizada":
      return lastSyncedAt === null
        ? { respuesta: "No", motivo: "Este brazo todavía no se sincronizó." }
        : { respuesta: "No", motivo: "No vino en la última sincronización." }
  }
}

/**
 * ⭐⭐ **EL ESTADO DE META, EN ESPAÑOL — Y CON EL CRUDO A LA VISTA CUANDO NO SE
 * CONOCE.**
 *
 * `PRD_R1.1.md:56` (§1.1, «Ajustes de interfaz») pide *«Estados de plantilla en
 * español (Aprobada, Pendiente, Rechazada, Pausada)»*, y `CA-48`…`CA-51` fijan
 * los cuatro por su nombre.
 *
 * ⛔⛔ **ESTO DEROGA `guard` ⑥, Y LA MITAD QUE AQUEL GUARD PROTEGÍA SE CONSERVA.**
 * `pantalla.tsx` decía, textual: *«EL LITERAL DE META, CRUDO Y SIN TRADUCIR […]
 * Una traducción crearía un segundo vocabulario para los mismos valores, y el día
 * que Meta agregue uno la traducción no lo tiene y **acá quedaría un hueco justo
 * en el estado que alguien está buscando**»*. **Ese riesgo es real y sigue en
 * pie**: lo que se deroga es la conclusión, no el análisis.
 *
 * ⇒ la tabla traduce **los cuatro que el PRD nombra** y, para cualquier otro,
 * **devuelve el literal de Meta tal cual**. Así:
 *   · el operador lee «Aprobada» en los casos que el PRD pide (`CA-48`…`CA-51`);
 *   · y el día que Meta invente un quinto estado, la celda muestra **su nombre**,
 *     que es lo que el operador puede buscar en el WhatsApp Manager — no un
 *     hueco, no un «Desconocido», no la primera opción de una lista blanca.
 *
 * ⛔ La comparación es sobre el valor **normalizado a mayúsculas**: Meta emite
 * `APPROVED`, y el contrato no promete la caja. Normalizar acá es distinto de
 * normalizar el brazo de una ruta —esto es un valor que llega de un tercero, no
 * una dirección que el operador tipea—.
 */
const ESTADO_EN_ESPANOL: Record<string, string> = {
  /*
   * ⭐ `APPROVED` sale del SÍMBOLO y no de un literal: es el mismo valor que
   * `veredictoDelEstado()` compara, y `tsc` lo sostiene contra
   * `contracts/channel` (`TEMPLATE_APPROVED_STATUS`). Si el contrato cambiara ese
   * literal, la clave de esta tabla cambia con él.
   *
   * ⚠️ Los otros tres **no tienen símbolo en ningún contrato de este repo**
   * —medido: `contracts/channel` sólo nombra `APPROVED`— así que van como
   * literales, con su costo declarado: si Meta renombrara `PAUSED`, esta tabla no
   * lo tendría y la celda mostraría el crudo. Que muestre el crudo es
   * exactamente el comportamiento correcto, no la falla.
   */
  [ESTADO_APROBADA_EN_META]: "Aprobada",
  PENDING: "Pendiente",
  REJECTED: "Rechazada",
  PAUSED: "Pausada",
}

export function estadoEnEspanol(approvalStatus: string): string {
  return ESTADO_EN_ESPANOL[approvalStatus.toUpperCase()] ?? approvalStatus
}

/**
 * ⭐ `R2-F2-CA-08` · **La columna «Estado en Meta» de una fila cuyo catálogo no
 * la tiene.**
 *
 * `null` acá NO es «Sin estado» ni una celda vacía: es el hecho de que la última
 * sincronización no la trajo, y decirlo es media pantalla. Ver `FilaParaEnvios`:
 * el cruce lo hace el borde y esto sólo lo redacta.
 *
 * ⚠️ **El texto es el MISMO que el motivo de `CA-08`, y la repetición está
 * prevista**: `PRD_R2.md:847` dice que una fila muestre «No vino en la última
 * sincronización» en las dos columnas *«y hay que leer sin sorprenderse»*. No se
 * abrevia una de las dos para que se vean distintas.
 */
export function estadoEnMeta(catalog: PlantillaDelBrazo | null): string {
  if (catalog === null) return "No vino en la última sincronización"
  return estadoEnEspanol(catalog.approvalStatus)
}

/**
 * ⭐⭐ **LA CATEGORÍA, EN ESPAÑOL — Y EL VALOR NO PREVISTO SE MUESTRA TAL CUAL.**
 *
 * `PRD_R2.md:816` nombra las tres y el caso sin categoría. Es la misma disciplina
 * que `estadoEnEspanol`, por el mismo motivo y con el mismo guard:
 *
 * ⛔⛔ **Un `switch` con `default: "Sin categoría"` es la implementación
 * equivocada, y es la que sale sola** porque el PRD sólo escribe cuatro casos. Una
 * categoría nueva de Meta —que las hay, el vocabulario es de ellos y ninguna
 * fuente del repo lo enumera— se mostraría como «sin categoría», que es **una
 * afirmación falsa**: la plantilla SÍ tiene categoría, y el operador que la vea
 * así va a creer que Meta no la trajo. La regla 10 de `R2-F2` dice tal cual.
 *
 * ⇒ `null` —y sólo `null`— es «Sin categoría»; cualquier string que la tabla no
 * conozca se muestra con su nombre.
 */
const CATEGORIA_EN_ESPANOL: Record<string, string> = {
  MARKETING: "Marketing",
  UTILITY: "Utilidad",
  AUTHENTICATION: "Autenticación",
}

export function categoriaEnEspanol(category: string | null): string {
  if (category === null) return "Sin categoría"
  return CATEGORIA_EN_ESPANOL[category.toUpperCase()] ?? category
}

/**
 * El tipo de un botón, en español. Mismo criterio que la categoría: los dos que
 * el PRD nombra (`CA-11`, `CA-12`) y el resto tal cual.
 *
 * ⚠️ **Los dos literales son NO VERIFICADOS.** Se toman del contrato de
 * `R2-CCH1`, que los toma de la documentación de la Graph API; **no hay captura
 * de una respuesta real de Meta en este repo**. Si el día de mañana llegan en otra
 * caja o con otro nombre, la celda muestra el crudo —que es lo correcto— y nadie
 * se queda sin poder leer la fila.
 */
const TIPO_DE_BOTON_EN_ESPANOL: Record<string, string> = {
  QUICK_REPLY: "Respuesta rápida",
  URL: "Enlace",
}

export function tipoDeBotonEnEspanol(type: string): string {
  return TIPO_DE_BOTON_EN_ESPANOL[type.toUpperCase()] ?? type
}

/**
 * ⭐ `CA-11` / `CA-12` · **«N · Tipo · Texto» y, si lleva, «· url»**
 * (`PRD_R2.md:820`).
 *
 * ⛔ **El número es `boton.position` y NO el índice del arreglo.** Son dos cosas
 * que casi siempre coinciden y que el día que no coincidan mandan al operador a
 * configurar OTRO botón: Meta identifica el botón por su posición, que arranca en
 * 1 y viene en el dato (`R2-CCH1`). Un `i + 1` sería un número inventado que se ve
 * igual, y ningún examen del repo lo podría distinguir —ninguna fuente enumera
 * botones contra los cuales comparar—.
 */
export function botonParaMostrar(boton: BotonDePlantilla): string {
  const base = `${boton.position} · ${tipoDeBotonEnEspanol(boton.type)} · ${boton.text}`
  return boton.url === null ? base : `${base} · ${boton.url}`
}

/**
 * ⭐ `CA-03` / `CA-04` / `CA-05` · **Las variables que la plantilla declara, en
 * orden y entre llaves dobles** (`PRD_R2.md:817`).
 *
 * `TemplateRecord.variables` guarda **los NOMBRES** —`"1"`, `"2"`, `"nombre"`—, no
 * el marcador; las llaves son de la presentación, y es lo que el PRD escribe:
 * *«`{{1}}`, `{{2}}`. Si vienen con nombre, ese nombre: `{{nombre}}`»*.
 *
 * ⛔ **Se conserva el orden y no se deduplica**: el orden es el del relleno
 * posicional de Meta.
 *
 * ⚠️ **El arreglo vacío devuelve el arreglo vacío y no el texto de `CA-04`.** Quien
 * dibuja decide si eso es «Sin variables» o una lista, porque el mismo dato lo
 * usan la tabla y el panel «Agregar plantilla» con dos formas distintas.
 */
export function variablesParaMostrar(catalog: PlantillaDelBrazo | null): readonly string[] {
  if (catalog === null) return []
  return catalog.variables.map((nombre) => `{{${nombre}}}`)
}
